import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { ChatModel } from '@agent/agent-interface/contact-view/chat/model/chat-model';
import { ScreenSharingStatus } from '@agent/agent-interface/contact-view/chat/model/screen-sharing-info';
import { ChatActionsService } from '@agent/agent-interface/contact-view/chat/services/chat-actions.service';
import { ChatAttachmentService } from '@agent/agent-interface/contact-view/chat/services/chat-attachment.service';
import { ScreenSharingService } from '@agent/agent-interface/contact-view/chat/services/screen-sharing.service';
import { OutgoingMailDeprecatedService } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { AppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { UnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { NG1_APPOINTMENT, NG1_MAIL_UNIT, NG1_PHONE_UNIT } from '@commons/core/core.provider.ajs';
import { first } from 'rxjs';

import {
  Booking,
  BookingFacade,
  ChannelDirection,
  ChannelType,
  DisplayDeprecatedComponent,
  Option,
  ProfileService,
  SmsFacade,
  TranslateService,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

import { ChatSocketService } from '../../services/chat-socket.service';
import { AgentBookingChatInprocessState } from '../../state/agent-booking-chat-routing.module';

@UntilDestroy()
@Component({
  selector: 'chat-actions',
  templateUrl: 'chat-actions.component.html',
  styleUrls: ['./chat-actions.component.scss'],
})
export class ChatActionsComponent extends DisplayDeprecatedComponent implements OnInit {
  @Input()
  responseId: string;

  public enableTransfer: boolean = false;
  public enableConference: boolean = false;
  public enableMirroring: boolean = false;
  public canRedirectToScriptAnchor: boolean = false;
  public havePhoneAddress: boolean = false;
  public haveMailAddress: boolean = false;
  public toggleMoreMenu: boolean = false;

  private selectedContact: Booking | undefined;

  public get chat(): ChatModel | undefined {
    return this.chatSocketService.getChatByResponseId(this.responseId);
  }

  constructor(
    public readonly chatSocketService: ChatSocketService,
    private readonly chatActions: ChatActionsService,
    private readonly profileService: ProfileService,
    private readonly attachmentService: ChatAttachmentService,
    private readonly smsFacade: SmsFacade,
    @Inject(NG1_MAIL_UNIT) private readonly outgoingMailDeprecatedFacade: OutgoingMailDeprecatedService,
    @Inject(NG1_APPOINTMENT) private readonly appointmentDeprecatedService: AppointmentDeprecatedService,
    @Inject(NG1_PHONE_UNIT) private readonly unitOutgoingCallDeprecatedService: UnitOutgoingCallDeprecatedService,
    private readonly screenSharingService: ScreenSharingService,
    private readonly contactFacade: BookingFacade,
    private readonly translateService: TranslateService,
    private readonly router: AgentDesktopRouter
  ) {
    super();
  }

  ngOnInit() {
    this.profileService
      .hasPermission$('CSR_CHAT_INCOMING::contact:transfer_to_skill')
      .subscribe(right => (this.enableTransfer = this.enableTransfer || right));
    this.profileService
      .hasPermission$('CSR_CHAT_INCOMING::contact:transfer')
      .subscribe(right => (this.enableTransfer = this.enableTransfer || right));
    this.profileService.hasPermission$('CSR_CHAT_INCOMING::contact:conference').subscribe(right => (this.enableConference = right));
    this.profileService.hasPermission$('CSR_CHAT_INCOMING::mirroring:activate').subscribe(right => (this.enableMirroring = right));
    this.profileService
      .hasPermission$('CSR_CHAT_INCOMING::contact:redirect_to_script_anchor_with_csr_release')
      .subscribe(right => (this.canRedirectToScriptAnchor = right));

    this.checkCustomerCardUri();

    this.contactFacade.selected$.subscribe(contact => (this.selectedContact = contact));
  }

  closeChat(): void {
    let result = true;
    if (!this.attachmentService.canLogout()) {
      result = window.confirm(this.translateService.instant('agent:panel.chat.attachment.close_confirm_message'));
    }
    if (result) {
      this.chatActions.hangUp(this.responseId);
      if (this.chat) {
        this.attachmentService.removeInfo(this.chat.customerId);
      }
    }
  }

  leaveConference(): void {
    this.chatActions.hangUp(this.responseId);
  }

  showActions(): boolean {
    return !!this.chat;
  }

  transfer(isConference: boolean) {
    const chat = this.chat;
    if (chat && isConference) {
      this.contactFacade.update(this.responseId, {
        conference: isConference,
      });
      chat.conference = true;
      chat.inConferenceMode = true;

      this.router.navigateFromLastChild(['..', AgentBookingChatInprocessState.conference, isConference]);
    } else if (chat) {
      chat.inTransferMode = true;

      this.router.navigateFromLastChild(['..', AgentBookingChatInprocessState.forward, isConference]);
    }
  }

  scriptRedirect() {
    this.router.navigateFromLastChild(['..', AgentBookingChatInprocessState.scriptRedirection]);
  }

  getConferenceButton() {
    return this.chat && this.chat.conference;
  }

  get closeFromVisitor() {
    return this.chat && this.chat.tab.closedByClient;
  }

  get conversationCloseByTheClient() {
    return this.chat && this.chat.tab.conversationEndByClient;
  }

  public get screenSharingDemandPending(): boolean {
    const activeChat = this.chat;
    return activeChat ? activeChat.screenSharingInfos.status === ScreenSharingStatus.PENDING_INVITATION : false;
  }

  public get alreadySharingScreen(): boolean {
    const activeChat = this.chat;
    return activeChat ? activeChat.screenSharingInfos.status === ScreenSharingStatus.ONGOING : false;
  }

  public get hasScreenSharing(): boolean {
    const chat =
      this.chatSocketService.chatList.length > 1 &&
      this.chatSocketService.chatList.find(
        e =>
          e.screenSharingInfos.status === ScreenSharingStatus.ONGOING ||
          e.screenSharingInfos.status === ScreenSharingStatus.PENDING_INVITATION
      );
    if (chat != null) {
      return this.chatSocketService.chatList.length > 1 && chat !== this.chatSocketService.activeChat;
    }
    return false;
  }

  public get visitorHasNoCapabilityForScreenSharing(): boolean {
    const activeChat = this.chat;
    return !!activeChat && !activeChat.checkCapabilityForScreenSharing;
  }

  public askForScreenSharing(): void {
    this.screenSharingService.askForScreenSharingSession();
  }

  public forceStopScreenSharing(): void {
    if (this.chat) {
      this.screenSharingService.stopScreenSharing(this.chat);
    }
  }

  checkCustomerCardUri() {
    this.havePhoneAddress = false;
    this.contactFacade.getCustomerAddressesById(this.responseId).subscribe(addresses => {
      addresses.forEach(add => {
        switch (add.channel.type) {
          case ChannelType.PHONE:
            this.havePhoneAddress = true;
          case ChannelType.MAIL:
            this.haveMailAddress = true;
        }
      });
    });
  }

  openSMSForm(): void {
    if (this.selectedContact) {
      this.smsFacade.openDialog({
        customerId: this.selectedContact.customerId,
      });
    }
  }

  canOpenUnitMail(): boolean {
    return this.outgoingMailDeprecatedFacade.canOpenOutgoingMail();
  }

  openUnitMail(): void {
    if (this.selectedContact) {
      this.outgoingMailDeprecatedFacade.openOutgoingMailForContactUuid(
        Option.of(this.selectedContact.customerId).getOrElse(this.selectedContact.customerId || ''),
        this.responseId
      );
    }
  }

  openAppointmentForm(): void {
    if (this.selectedContact) {
      this.contactFacade
        .getCustomerById(this.selectedContact.responseId)
        .pipe(first(), untilDestroyed(this))
        .subscribe(customer => {
          if (this.selectedContact) {
            let firstName: string;
            let lastName: string;
            if (!!customer) {
              firstName = customer.firstname;
              lastName = customer.lastname;
            } else {
              firstName = this.selectedContact.customerName;
              lastName = '';
            }
            this.appointmentDeprecatedService.openAppointmentForm(
              this.selectedContact.responseId,
              this.selectedContact.phoneNumber,
              firstName,
              lastName,
              this.selectedContact.skills
            );
          }
        });
    }
  }

  canOpenOutcall(): boolean {
    return this.unitOutgoingCallDeprecatedService.canOpenOutcall();
  }

  openOutcall() {
    this.openOutcallAction();
  }

  get outcallRequested(): boolean {
    if (this.unitOutgoingCallDeprecatedService.unitOutgoingCall != null) {
      return this.unitOutgoingCallDeprecatedService.unitOutgoingCall.requested;
    }
    return false;
  }

  private openOutcallAction(): void {
    if (!this.outcallRequested) {
      this.contactFacade.getCustomerById(this.responseId).subscribe(
        customer => {
          this.unitOutgoingCallDeprecatedService.openUnitOutgoingCallFormFromInteraction(this.responseId, customer);
        },
        () => this.unitOutgoingCallDeprecatedService.finishAndClosePauseAndTask()
      );
    }
  }

  outcallDisabled(): boolean {
    return this.unitOutgoingCallDeprecatedService.outcallDisabled();
  }

  hasPermission(permission): boolean {
    return this.profileService.hasPermission(permission);
  }

  fileUploadInProgress(): boolean {
    return this.attachmentService.canLogout();
  }
}
